import { z } from "zod";
import { API_ERROR, type ApiErrorCode } from "../apiErrors";
import {
  GOOGLE_IMPORT_MAX_REVIEWS,
  GOOGLE_REVIEW_AUTHOR_MAX,
  GOOGLE_REVIEW_ID_PATTERN,
  GOOGLE_REVIEW_TEXT_MAX,
  isGoogleReviewTime,
} from "../googleReviews";
import { TRANSLATION_MARKERS } from "../takeout/originalReviewText";
import { isIsoDate } from "../utils";

/**
 * Požadavky tras Google recenzí (vize F2). Vlastnost, na které stojí trasy importu
 * i správy: text KAŽDÉHO issue je kód z registru (`isApiErrorCode(issue.message)`),
 * takže odpověď 400 nese jen kódy a nikdy výchozí anglický text Zodu s odeslanou hodnotou
 * (jméno a text cizích lidí nesmějí skončit v odpovědi ani v logu, F2.1).
 *
 * Proto má každý uzel, který sám vydává issue (typ objektu, pole, výčtu, unie), vlastní
 * `errorMap` s kódem (`coded`), a každá kontrola (`min`, `max`, `regex`, `refine`) kód
 * v argumentu. `required_error`/`invalid_type_error` nestačí: nepokryjí `invalid_enum_value`
 * ani `invalid_union`. Kontextová mapa v `safeParse(…, { errorMap })` by zase přebila
 * `required_error` u `location` (kód `google_import_no_location`).
 */
function coded(code: ApiErrorCode): { errorMap: z.ZodErrorMap } {
  return { errorMap: () => ({ message: code }) };
}

/** Chybějící hodnota → `missing`, jakýkoli jiný problém uzlu → `other`. */
function codedMissing(missing: ApiErrorCode, other: ApiErrorCode): { errorMap: z.ZodErrorMap } {
  return { errorMap: (_issue, ctx) => ({ message: ctx.data === undefined ? missing : other }) };
}

/** Značky strojového překladu Googlu — do API smí jen původní text autora (F2.2). */
export function hasGoogleTranslationMarker(text: string): boolean {
  return TRANSLATION_MARKERS.some((marker) => text.includes(marker));
}

// ── Import exportu Takeout (`POST /api/admin/google-reviews/import/{preview,confirm}`) ──

const importString = () => z.string(coded(API_ERROR.google_import_invalid));

const takeoutLocationSchema = z
  .object(
    {
      locationKey: importString()
        .trim()
        .min(1, API_ERROR.google_import_invalid)
        .max(200, API_ERROR.google_import_invalid),
      name: importString()
        .trim()
        .min(1, API_ERROR.google_import_invalid)
        .max(500, API_ERROR.google_import_invalid),
      placeId: importString().max(500, API_ERROR.google_import_invalid).nullable(),
    },
    // Chybějící i jinak neplatná lokace (null, řetězec, pole) = „požadavek lokaci nenese".
    coded(API_ERROR.google_import_no_location),
  )
  .strict(API_ERROR.google_import_invalid);

const starCountSchema = z.union(
  [z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)],
  coded(API_ERROR.google_import_invalid),
);

const takeoutReviewSchema = z
  .object(
    {
      id: importString().regex(GOOGLE_REVIEW_ID_PATTERN, API_ERROR.google_import_invalid),
      stars: starCountSchema,
      hasText: z.boolean(coded(API_ERROR.google_import_invalid)),
      author: importString()
        .trim()
        .min(1, API_ERROR.google_import_invalid)
        .max(GOOGLE_REVIEW_AUTHOR_MAX, API_ERROR.google_import_invalid)
        .optional(),
      text: importString()
        .trim()
        .min(1, API_ERROR.google_import_invalid)
        .max(GOOGLE_REVIEW_TEXT_MAX, API_ERROR.google_import_invalid)
        .optional(),
      createTime: importString()
        .refine(isGoogleReviewTime, API_ERROR.google_import_invalid)
        .optional(),
    },
    coded(API_ERROR.google_import_invalid),
  )
  .strict(API_ERROR.google_import_invalid)
  .superRefine((review, ctx) => {
    // Jméno, text a čas nese PRÁVĚ kvalifikovaná recenze (pět hvězd s textem); u ostatních
    // jsou zakázané — osobní údaje bez účelu neopustí prohlížeč (F2.1).
    const qualified = review.stars === 5 && review.hasText;
    for (const field of ["author", "text", "createTime"] as const) {
      const present = review[field] !== undefined;
      if (present !== qualified) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: API_ERROR.google_import_invalid,
        });
      }
    }
    if (review.text !== undefined && hasGoogleTranslationMarker(review.text)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["text"],
        message: API_ERROR.google_import_translation_only,
      });
    }
  });

export const googleImportRequestSchema = z
  .object(
    {
      location: takeoutLocationSchema,
      exportDate: importString().refine(isIsoDate, API_ERROR.google_import_invalid),
      exportDateSource: z.enum(["filename", "upload"], coded(API_ERROR.google_import_invalid)),
      reviews: z
        .array(takeoutReviewSchema, coded(API_ERROR.google_import_invalid))
        .max(GOOGLE_IMPORT_MAX_REVIEWS, API_ERROR.google_import_too_many),
    },
    coded(API_ERROR.google_import_invalid),
  )
  .strict(API_ERROR.google_import_invalid);

// ── Správa (`/api/admin/google-reviews`) ──

/**
 * Dnešní den v UTC (`YYYY-MM-DD`) — hranice „datum ruční recenze není v budoucnu".
 * Tentýž den bere admin jako `max` pole data, aby výběr nedovolil den, který resolver
 * (toto schéma) odmítne.
 */
export function todayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Ruční zadání a úprava (F2.6). Hvězdy se nezadávají (ukládá se jen pětihvězdičková):
 * klíč `stars` = issue u pole `stars` s `google_review_stars_not_allowed`.
 *
 * `destinationId`: klíč chybí → server přiřadí podle textu (F2.7) a nezamkne (u `PUT`
 * zamčený řádek drží svou destinaci i zámek); `null` → bez destinace, zamčeno; id → ta
 * destinace, zamčeno.
 */
export const googleManualReviewSchema = z
  .object(
    {
      authorName: z
        .string(
          codedMissing(API_ERROR.google_review_author_required, API_ERROR.google_review_invalid),
        )
        .trim()
        .min(1, API_ERROR.google_review_author_required)
        .max(GOOGLE_REVIEW_AUTHOR_MAX, API_ERROR.google_review_invalid),
      reviewDate: z
        .string(codedMissing(API_ERROR.google_review_date_invalid, API_ERROR.google_review_invalid))
        .refine(isIsoDate, API_ERROR.google_review_date_invalid)
        .refine((day) => day <= todayUtc(), API_ERROR.google_review_date_invalid),
      content: z
        .string(
          codedMissing(API_ERROR.google_review_text_required, API_ERROR.google_review_invalid),
        )
        .trim()
        .min(1, API_ERROR.google_review_text_required)
        .max(GOOGLE_REVIEW_TEXT_MAX, API_ERROR.google_review_invalid),
      destinationId: z
        .string(coded(API_ERROR.google_review_invalid))
        .min(1, API_ERROR.google_review_invalid)
        .nullable()
        .optional(),
      // Přítomný klíč `stars` (jakákoli hodnota) = vlastní kód u pole, ne obecné „neznámé pole".
      stars: z.never(coded(API_ERROR.google_review_stars_not_allowed)).optional(),
    },
    coded(API_ERROR.google_review_invalid),
  )
  .strict(API_ERROR.google_review_invalid)
  .transform(({ stars: _stars, ...review }) => review);

export type GoogleManualReviewInput = z.infer<typeof googleManualReviewSchema>;

/** Oprava destinace (`PATCH /:id/destination`); `null` = bez destinace, také zamčeno. */
export const googleReviewDestinationSchema = z
  .object(
    {
      destinationId: z
        .string(coded(API_ERROR.google_review_invalid))
        .min(1, API_ERROR.google_review_invalid)
        .nullable(),
    },
    coded(API_ERROR.google_review_invalid),
  )
  .strict(API_ERROR.google_review_invalid);

/** Návrh destinace z textu (`POST /destination-suggestion`); nic nezapisuje. */
export const googleDestinationSuggestionSchema = z
  .object(
    {
      text: z
        .string(coded(API_ERROR.google_review_invalid))
        .max(GOOGLE_REVIEW_TEXT_MAX, API_ERROR.google_review_invalid),
    },
    coded(API_ERROR.google_review_invalid),
  )
  .strict(API_ERROR.google_review_invalid);
